(() => {
  // Variable to track the currently playing video globally
let currentlyPlayingVideo = null;

// Intersection Observer for viewport detection
const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        const video = entry.target;
        // Pause if not in viewport AND it is currently playing
        if (!entry.isIntersecting && !video.paused) {
            video.pause();
            // Update the UI control for the paused video
            updateVideoControls(video);
        }
    });
}, { threshold: 0.8 }); // Adjust threshold as needed (e.g., 0.8 means 80% must be visible)


function pauseOtherVideos(currentVideo) {
    if (currentlyPlayingVideo && currentlyPlayingVideo !== currentVideo && !currentlyPlayingVideo.paused) {
        currentlyPlayingVideo.pause();
        updateVideoControls(currentlyPlayingVideo);
    }
    currentlyPlayingVideo = currentVideo;
}

function updateVideoControls(video) {
    if (!video) return;

    const slideItem = video.closest('.slide-item');
    const playPauseBtn = slideItem?.querySelector('.video-play-pause');
    const muteToggleBtn = slideItem?.querySelector('.video-mute-toggle');
    const playIcon = playPauseBtn?.querySelector('.icon-play');
    const pauseIcon = playPauseBtn?.querySelector('.icon-pause');
    const muteIcon = muteToggleBtn?.querySelector('.icon-mute');
    const unmuteIcon = muteToggleBtn?.querySelector('.icon-unmute');

    // Update Play/Pause Icon
    if (playIcon && pauseIcon) {
        playIcon.style.display = video.paused ? 'block' : 'none';
        pauseIcon.style.display = video.paused ? 'none' : 'block';
    }
    
    // Update Mute/Unmute Icon
    if (muteIcon && unmuteIcon) {
        muteToggleBtn.classList.toggle('is-muted', video.muted);
        muteIcon.style.display = video.muted ? 'block' : 'none';
        unmuteIcon.style.display = video.muted ? 'none' : 'block';
    }
}

// Function to attach all control listeners to a single video
function setupVideoListeners(video) {
    const slideItem = video.closest('.slide-item');
    if (!slideItem) return;

    // Skip setup if listeners are already attached (essential for editor reloads)
    if (video.dataset.videoListeners === 'true') return;
    video.dataset.videoListeners = 'true'; // Mark as setup

    const playPauseBtn = slideItem.querySelector('.video-play-pause');
    const muteToggleBtn = slideItem.querySelector('.video-mute-toggle');

    // 1. Play/Pause Click Handler
    playPauseBtn?.addEventListener('click', () => {
        if (video.paused) {
            pauseOtherVideos(video); // Pause others before playing
            video.play();
        } else {
            video.pause();
            currentlyPlayingVideo = null; // Clear global state if user manually pauses
        }
        updateVideoControls(video);
    });

    // 2. Mute/Unmute Click Handler
    muteToggleBtn?.addEventListener('click', () => {
        video.muted = !video.muted;
        updateVideoControls(video);
    });

    // 3. Update UI on video state change (play, pause, volumechange)
    // We listen to these events to keep the UI in sync after user interaction
    ['loadeddata', 'play', 'pause', 'volumechange'].forEach(event => {
        video.addEventListener(event, () => updateVideoControls(video));
    });

    // --- CRITICAL INITIALIZATION LOGIC ---
    
    // Define a function to set the initial state (paused/muted) and update the UI
    const initializeVideoState = () => {
        // Enforce the initial state (muted and paused as per Liquid settings)
        video.muted = true;
        video.pause();
        
        // This is the key: Update the icons immediately based on the enforced state
        updateVideoControls(video);
    };

    // A. The safe way: Wait for the video's metadata to load
    // This is necessary if the script loads before the video resource is ready.
    video.addEventListener('loadedmetadata', initializeVideoState);
    
    // B. The catch-up check: If the video has already loaded before the script
    // readyState >= 1 means metadata is available. This fixes the on-load icon issue.
    if (video.readyState >= 1) { 
        // Remove the listener to prevent it from running twice
        video.removeEventListener('loadedmetadata', initializeVideoState);
        initializeVideoState();
    }
    
    // --- End of Initialization Logic ---

    // 4. Attach to Intersection Observer
    videoObserver.observe(video);
}

  function readConfig(el) {
    const json = el.getAttribute('data-splide');
    if (json) {
      try {
        return JSON.parse(json);
      } catch (e) {
        console.warn('[Splide] Invalid data-splide JSON:', e, json);
      }
    }
    const perDesktop = parseInt(el.dataset.perDesktop || '4', 10);
    const perMobile = parseInt(el.dataset.perMobile || '1', 10);
    return {
      perPage: perDesktop,
      perMove: 1,
      gap: '1rem',
      pagination: false,
      arrows: true,
      breakpoints: { 768: { perPage: perMobile } },
    };
  }

  function initSplideFor(root) {
    const els = root.querySelectorAll ? root.querySelectorAll('.custom-splide.splide') : [];
    els.forEach((el) => {
      if (el.dataset.mounted === 'true') return;

      const cfg = readConfig(el);
      const s = new Splide(el, cfg);
      s.mount();
      el.dataset.mounted = 'true';
      
      // Find all video elements within this Splide instance and set up controls
        el.querySelectorAll('.splide__slide video').forEach(video => {
            // Prevent re-initialization if section loads multiple times in editor
            if (!video.dataset.videoListeners) {
                setupVideoListeners(video);
                video.dataset.videoListeners = 'true'; // Mark as setup
            }
        });

      // ✅ Progress bar targeting
      const progressBar = el.parentElement.querySelector('.splide-carousel-progress-bar');
      if (progressBar) {
        const updateProgress = () => {
          const totalSlides = s.length - s.options.perPage;
          const perPage = s.options.perPage;
          const length = s.length;
          const currentIndex = s.index;

          const progress = totalSlides > 0
            ? ((currentIndex + perPage) / length) * 100
            : 0;

          progressBar.style.width = `${progress}%`;
        };

        // Run on mount and slide move
        updateProgress();
        s.on('mounted move', updateProgress);
      }
    });
  }

  if (window.Splide) initSplideFor(document);
  else document.addEventListener('DOMContentLoaded', () => initSplideFor(document));

  document.addEventListener('shopify:section:load',    (e) => initSplideFor(e.target));
  document.addEventListener('shopify:section:reorder', (e) => initSplideFor(e.target));
  document.addEventListener('shopify:block:select',    (e) => initSplideFor(e.target));
})();
